import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { TooltipDirective } from '../../../../shared/components/ui/tooltip/tooltip.directive';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faUser } from '@fortawesome/pro-duotone-svg-icons';
// Solid variants for the meta chips — duotone glyphs are too airy at this
// size to read against the floor drawing.
import { faClock, faKey, faLock } from '@fortawesome/pro-solid-svg-icons';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { Room } from '../../../../state/rooms.repository';
import { isDoorClosed } from '../door-state';
import { LanguageService } from '../../../../core/services/language.service';
import {
  DOOR_GEOMETRIES,
  DoorSide,
  SEAT_RADIUS,
  seatPositions,
  SIDE_DOOR_TABLE_SHIFT,
  TABLE,
  VIEW_BOX,
  VIEW_BOX_HEIGHT,
} from '../room-plan-geometry';

/** Around-the-table dots cap. */
const MAX_SEATS = 8;

/**
 * A single room rendered as a playful floor-plan tile: colored walls with a
 * door on the bottom (corridor) side — open when the user can join directly,
 * closed when they would have to knock — and a table whose surrounding dots
 * are the connected peers. Purely presentational; no click behaviour yet.
 */
@Component({
  selector: 'andor-room-plan-tile',
  standalone: true,
  imports: [TooltipDirective, FaIconComponent, TranslatePipe],
  templateUrl: './room-plan-tile.html',
  styleUrl: './room-plan-tile.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoomPlanTile {
  readonly room = input.required<Room>();
  /** Which wall holds the door — decided by the container from grid adjacency. */
  readonly doorSide = input.required<DoorSide>();
  /** Ticking epoch ms from the container; drives schedule-based door state. */
  readonly now = input.required<number>();
  /** The own user skips the knock, so gated rooms draw an open door. */
  readonly bypassesGate = input(false);
  /** Full-opacity accent — the picked room or a search hit. */
  readonly highlighted = input(false);

  private readonly _translate = inject(TranslateService);
  // Read before `instant()` so labels re-translate when the language changes.
  private readonly _lang = inject(LanguageService).active;

  protected readonly doorClosed = computed(() =>
    isDoorClosed(this.room(), this.now(), this.bypassesGate())
  );
  protected readonly seats = computed(() =>
    seatPositions(Math.min(this.room().memberCount, MAX_SEATS))
  );
  protected readonly tooltip = computed(() => {
    this._lang();
    const room = this.room();
    const members = this._translate.instant('[count] members online', {
      count: room.memberCount,
    });
    const status = this.doorClosed()
      ? this._translate.instant('Knock to join — approval required')
      : this._translate.instant('Open — join directly');
    return `${room.name} · ${members} · ${status}`;
  });

  protected readonly doorGeo = computed(() => DOOR_GEOMETRIES[this.doorSide()]);

  // Side-door rooms have a free bottom band, so the whole table group (table,
  // seats, overflow badge) sits a bit lower there — closer to the true center.
  private readonly _tableShift = computed(() =>
    this.doorSide() === 'bottom' ? 0 : SIDE_DOOR_TABLE_SHIFT
  );
  protected readonly tableTransform = computed(() => `translate(0 ${this._tableShift()})`);
  /** Overflow badge center as a percentage of the tile height (HTML overlay). */
  protected readonly badgeTop = computed(
    () => ((TABLE.y + TABLE.height / 2 + this._tableShift()) / VIEW_BOX_HEIGHT) * 100
  );

  protected readonly viewBox = VIEW_BOX;
  protected readonly table = TABLE;
  protected readonly seatRadius = SEAT_RADIUS;

  protected readonly faLock = faLock;
  protected readonly faKey = faKey;
  protected readonly faClock = faClock;
  protected readonly faUser = faUser;
}
